// src/ui/components/XRayOverlay.tsx
//
// 「表は単純、裏は本物」を体感させるためのショーケース機能(2.5節)。
// Next Gen Award(学生対象・動画+コード審査)では課金機能は不要と判断し、
// 誰でも無料で使える形で公開する。
// 通常モードでは隠されているEKFの不確かさ(共分散)や、逆動力学が計算した
// 力ベクトルをそのまま可視化する。UIの装飾ではなく、実際にエンジンが
// 内部で保持している数値をそのまま描画することが体験の核。

import { useEffect, useRef } from "react";
import { colors } from "../design-system/colors";
import type { BiomechResult } from "../../workers/kinematics.worker";

export interface XRayFrameData {
  width: number;
  height: number;
  hipScreen: { x: number; y: number };
  shoulderScreen: { x: number; y: number };
  hipCovTrace: number;
  biomech: BiomechResult;
}

export default function XRayOverlay({ data }: { data: XRayFrameData | null }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx || !data) {
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
      return;
    }

    canvas.width = data.width;
    canvas.height = data.height;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // --- EKF共分散「楕円」(不確かさの可視化) ---
    const ellipseRadius = 10 + Math.min(60, data.hipCovTrace * 4000);
    ctx.strokeStyle = colors.xray.covariance;
    ctx.setLineDash([4, 4]);
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.ellipse(data.hipScreen.x, data.hipScreen.y, ellipseRadius, ellipseRadius * 0.7, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);

    // --- L4/L5にかかる力ベクトル(圧縮=下向き矢印の太さ、せん断=横向き矢印) ---
    const scale = 0.03;
    const compLen = Math.min(120, data.biomech.l4l5_compression_n * scale);
    const shearLen = Math.min(120, data.biomech.l4l5_shear_n * scale);

    drawArrow(
      ctx,
      data.hipScreen.x,
      data.hipScreen.y,
      data.hipScreen.x,
      data.hipScreen.y + compLen,
      colors.xray.forceVector,
    );
    drawArrow(
      ctx,
      data.hipScreen.x,
      data.hipScreen.y,
      data.hipScreen.x + shearLen,
      data.hipScreen.y,
      colors.xray.lumbarReadout,
    );

    // --- 数値ラベル(実際にエンジンが返した生の値) ---
    ctx.font = "12px 'JetBrains Mono', monospace";
    ctx.fillStyle = colors.xray.label;
    ctx.fillText(
      `L4/L5 compression: ${data.biomech.l4l5_compression_n.toFixed(1)} N`,
      12,
      data.height - 64,
    );
    ctx.fillText(`L4/L5 shear: ${data.biomech.l4l5_shear_n.toFixed(1)} N`, 12, data.height - 46);
    ctx.fillText(
      `erector spinae F: ${data.biomech.estimated_muscle_force_n.toFixed(1)} N`,
      12,
      data.height - 28,
    );
    ctx.fillText(
      `EKF cov trace: ${data.hipCovTrace.toExponential(2)}`,
      12,
      data.height - 10,
    );
  }, [data]);

  return (
    <canvas
      ref={canvasRef}
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
    />
  );
}

function drawArrow(
  ctx: CanvasRenderingContext2D,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  color: string,
) {
  const headLength = 8;
  const angle = Math.atan2(y2 - y1, x2 - x1);
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = 2.5;
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(
    x2 - headLength * Math.cos(angle - Math.PI / 6),
    y2 - headLength * Math.sin(angle - Math.PI / 6),
  );
  ctx.lineTo(
    x2 - headLength * Math.cos(angle + Math.PI / 6),
    y2 - headLength * Math.sin(angle + Math.PI / 6),
  );
  ctx.closePath();
  ctx.fill();
}
